import { ApiError } from "@brandex/api-client";
import { Alert, Button, Field, Input } from "@brandex/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { keys } from "../queries";

const PASSWORD_MIN = 12;

/* ----------------------------------------------------------------- Login */

const loginSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
  totpCode: z.string().regex(/^\d{6}$/).optional().or(z.literal("")),
});
type LoginValues = z.infer<typeof loginSchema>;

export function LoginForm({ onSuccess }: { onSuccess?: () => void }) {
  const { api, platform, nav } = useBrandex();
  const t = useT();
  const qc = useQueryClient();
  const [needsCode, setNeedsCode] = useState(false);
  const form = useForm<LoginValues>({ resolver: zodResolver(loginSchema), defaultValues: { email: "", password: "", totpCode: "" } });

  const login = useMutation({
    mutationFn: async (v: LoginValues) => {
      const { data } = await api.http.POST("/v1/auth/login", {
        body: {
          email: v.email,
          password: v.password,
          client: platform.client,
          totp_code: v.totpCode === "" || v.totpCode === undefined ? null : v.totpCode,
        },
      });
      if (data === undefined) throw new Error("Empty response");
      await api.acceptTokens(data);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.me, data.user);
      if (onSuccess) onSuccess();
      else nav.navigate("/app");
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "mfa_required") setNeedsCode(true);
    },
  });

  // `mfa_required` is conveyed by the code field's hint, not as an error.
  const showError =
    login.error !== null && !(login.error instanceof ApiError && login.error.code === "mfa_required");

  return (
    <form noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => login.mutate(v))}>
      {showError ? <Alert tone="error">{errorMessage(login.error, t)}</Alert> : null}
      <Field label={t("auth.email")} error={form.formState.errors.email ? t("error.validation_failed") : undefined}>
        <Input type="email" autoComplete="email" {...form.register("email")} />
      </Field>
      <Field label={t("auth.password")}>
        <Input type="password" autoComplete="current-password" {...form.register("password")} />
      </Field>
      {needsCode ? (
        <Field label={t("auth.totpCode")} hint={t("error.mfa_required")} error={form.formState.errors.totpCode ? t("error.validation_failed") : undefined}>
          <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus {...form.register("totpCode")} />
        </Field>
      ) : null}
      <Button type="submit" size="lg" loading={login.isPending}>{t("auth.signIn")}</Button>
    </form>
  );
}

/* ------------------------------------------------------- Forgot password */

export function ForgotPasswordForm() {
  const { api } = useBrandex();
  const t = useT();
  const form = useForm<{ email: string }>({ resolver: zodResolver(z.object({ email: z.email() })), defaultValues: { email: "" } });
  const send = useMutation({
    mutationFn: async (email: string) => {
      await api.http.POST("/v1/auth/password/forgot", { body: { email } });
    },
  });
  if (send.isSuccess) return <Alert tone="success">{t("auth.resetSent")}</Alert>;
  return (
    <form noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => send.mutate(v.email))}>
      {send.error !== null ? <Alert tone="error">{errorMessage(send.error, t)}</Alert> : null}
      <Field label={t("auth.email")} error={form.formState.errors.email ? t("error.validation_failed") : undefined}>
        <Input type="email" autoComplete="email" {...form.register("email")} />
      </Field>
      <Button type="submit" size="lg" loading={send.isPending}>{t("auth.sendResetLink")}</Button>
    </form>
  );
}

/* -------------------------------------------------------- Reset password */

/**
 * Sets a password from an emailed link: a reset (`reset`) or the first
 * password of an account BrandEx staff opened (`setup`, F-17).
 */
export function ResetPasswordForm({ userId, token, mode = "reset" }: { userId: string; token: string; mode?: "reset" | "setup" }) {
  const { api, nav } = useBrandex();
  const t = useT();
  const form = useForm<{ password: string }>({ resolver: zodResolver(z.object({ password: z.string().min(PASSWORD_MIN).max(128) })), defaultValues: { password: "" } });
  const reset = useMutation({
    mutationFn: async (password: string) => {
      const body = { user_id: userId, token, new_password: password };
      if (mode === "setup") await api.http.POST("/v1/auth/password/setup", { body });
      else await api.http.POST("/v1/auth/password/reset", { body });
    },
  });
  if (reset.isSuccess) {
    return (
      <div className="grid gap-4">
        <Alert tone="success">{t(mode === "setup" ? "auth.passwordSaved" : "auth.passwordUpdated")}</Alert>
        <Button onClick={() => nav.navigate("/auth/login")}>{t("auth.signIn")}</Button>
      </div>
    );
  }
  return (
    <form noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => reset.mutate(v.password))}>
      {mode === "setup" ? <p className="text-sm text-muted-foreground">{t("auth.setupIntro")}</p> : null}
      {reset.error !== null ? <Alert tone="error">{errorMessage(reset.error, t)}</Alert> : null}
      <Field label={t("auth.newPassword")} hint={t("auth.passwordHint")} error={form.formState.errors.password ? t("auth.passwordHint") : undefined}>
        <Input type="password" autoComplete="new-password" {...form.register("password")} />
      </Field>
      <Button type="submit" size="lg" loading={reset.isPending}>{t(mode === "setup" ? "auth.savePassword" : "auth.setPassword")}</Button>
    </form>
  );
}

/* ---------------------------------------------------------- Verify email */

export function VerifyEmail({ userId, token }: { userId: string; token: string }) {
  const { api } = useBrandex();
  const t = useT();
  const qc = useQueryClient();
  const verify = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/auth/verify-email", { body: { user_id: userId, token } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
  // Links are single-use: fire exactly once even under StrictMode double effects.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    verify.mutate();
  }, [verify]);

  if (verify.isSuccess) return <Alert tone="success">{t("auth.verified")}</Alert>;
  if (verify.isError) return <Alert tone="error">{t("auth.verifyFailed")}</Alert>;
  return <p role="status" className="text-sm text-muted-foreground">{t("auth.verifying")}</p>;
}
